import { openDB, IDBPDatabase } from 'idb';
import { uploadDraftAsset, deleteMedia } from './storageService';

const DB_NAME = 'dekko_drafts_db';
const DB_VERSION = 1;
const DRAFT_STORE = 'drafts';
/** Per-asset ceiling when pulling a remote draft back down. Generous — these
    are multi-MB files on poor connections — but never unbounded. */
const DRAFT_FETCH_TIMEOUT_MS = 60_000;
const BLOB_STORE = 'draft_blobs';

/**
 * Stored in the 'drafts' object store, keyed by taskId.
 * Media blobs live in a separate 'draft_blobs' store (auto-increment keys)
 * to keep the draft record lightweight for listing operations.
 */
export interface DraftLineage {
  parentSubmissionId: string;
  submissionType: 'redo' | 'addendum';
  attemptNumber: number;
}

interface StoredDraft {
  taskId: string;
  captureType: string;
  notes: string;
  textContent?: string;
  /** Local blob store keys. Empty when the draft fell back to remote. */
  blobKeys: number[];
  blobMimeTypes: string[];
  /** Remote fallback — Storage download URLs (parallel to blobPaths). */
  blobUrls?: string[];
  /** Storage object paths, for deletion. Parallel to blobUrls. */
  blobPaths?: string[];
  durations: number[];
  lineage?: DraftLineage;
  createdAt: number;
  updatedAt: number;
}

export interface DraftInput {
  captureType: string;
  notes: string;
  textContent?: string;
  blobs: Blob[];
  durations?: number[];
  lineage?: DraftLineage;
}

export interface DraftOutput {
  taskId: string;
  captureType: string;
  notes: string;
  textContent?: string;
  blobs: Blob[];
  durations: number[];
  lineage?: DraftLineage;
  /**
   * Remote draft media could not be fetched (offline). The draft's text is
   * intact but its media is NOT loaded — callers must not let the participant
   * submit as if it were. Retryable: a later attempt on a better connection
   * can succeed.
   */
  mediaUnavailable?: boolean;
  /**
   * Local draft media is gone from the device (evicted IndexedDB blobs) and
   * there is no remote copy. NOT retryable — no future attempt can recover
   * it. Callers should let the participant continue with the surviving
   * text/notes rather than dead-end them on a Resume that always errors.
   */
  mediaLost?: boolean;
  createdAt: number;
  updatedAt: number;
}

let dbPromise: Promise<IDBPDatabase> | null = null;

const getDB = (): Promise<IDBPDatabase> => {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(DRAFT_STORE)) {
          db.createObjectStore(DRAFT_STORE, { keyPath: 'taskId' });
        }
        if (!db.objectStoreNames.contains(BLOB_STORE)) {
          db.createObjectStore(BLOB_STORE, { autoIncrement: true });
        }
      },
    });
  }
  return dbPromise;
};

/**
 * Drop a draft's media — local blobs and any remote Storage objects.
 *
 * Best-effort throughout: a network failure must never block removing the
 * draft record, or a participant would be stuck with a draft they cannot
 * discard. The stale sweep is the backstop for anything left behind.
 */
const releaseMedia = async (
  db: IDBPDatabase,
  draft: StoredDraft | undefined,
): Promise<void> => {
  if (draft?.blobKeys?.length) {
    try {
      const tx = db.transaction(BLOB_STORE, 'readwrite');
      for (const key of draft.blobKeys) {
        await tx.store.delete(key);
      }
      await tx.done;
    } catch (err) {
      console.warn('[drafts] could not delete local draft blobs:', err);
    }
  }
  if (draft?.blobPaths?.length) {
    await Promise.all(draft.blobPaths.map(p => deleteMedia(p).catch(() => { })));
  }
};

/**
 * Save a draft to IndexedDB. Overwrites any existing draft for this taskId.
 * Stores actual blob bytes (not URLs) so drafts survive page reload.
 *
 * Local-first: on the browsers where IndexedDB accepts Blobs — nearly all of
 * them — this behaves and costs exactly as it always has. Only when the local
 * blob write is refused (iOS in-app browsers) does the media go to Firebase
 * Storage instead, leaving a small record IndexedDB will accept there.
 */
export const saveDraft = async (taskId: string, data: DraftInput): Promise<void> => {
  const db = await getDB();
  const existing = await db.get(DRAFT_STORE, taskId) as StoredDraft | undefined;

  // Drop zero-byte blobs (a recording that produced no chunks, an empty file).
  // getDraft cannot read them back — it skips size-0 entries — so storing one
  // makes blobs.length !== blobKeys.length forever, which permanently sets
  // mediaUnavailable and leaves the task stuck on a Resume button that always
  // errors. A draft with a broken recording must degrade to notes-only, not
  // lock the participant out of the task.
  const storableBlobs = data.blobs.filter(b => b.size > 0);

  let blobKeys: number[] = [];
  let blobUrls: string[] | undefined;
  let blobPaths: string[] | undefined;
  const blobMimeTypes = storableBlobs.map(b => b.type || 'application/octet-stream');

  if (storableBlobs.length > 0) {
    try {
      const tx = db.transaction(BLOB_STORE, 'readwrite');
      for (const blob of storableBlobs) {
        blobKeys.push(await tx.store.add(blob) as number);
      }
      await tx.done;
    } catch (localErr) {
      // iOS in-app browsers accept small records but refuse Blobs. Roll back
      // any partial writes so the draft is never half-local/half-remote, then
      // persist the media server-side instead.
      console.warn('[drafts] local blob store unavailable, using remote:', localErr);
      if (blobKeys.length) {
        try {
          const tx = db.transaction(BLOB_STORE, 'readwrite');
          for (const key of blobKeys) await tx.store.delete(key);
          await tx.done;
        } catch { /* best effort */ }
      }
      blobKeys = [];
      // If this throws too, so does saveDraft — which is the point: the
      // participant must be told, never shown a draft that isn't there.
      const uploaded = await Promise.all(
        storableBlobs.map((b, i) => uploadDraftAsset(b, taskId, i)),
      );
      blobUrls = uploaded.map(u => u.url);
      blobPaths = uploaded.map(u => u.path);
    }
  }

  const now = Date.now();
  try {
    await db.put(DRAFT_STORE, {
      taskId,
      captureType: data.captureType,
      notes: data.notes,
      textContent: data.textContent,
      blobKeys,
      blobMimeTypes,
      ...(blobUrls ? { blobUrls, blobPaths } : {}),
      durations: data.durations || [],
      ...(data.lineage ? { lineage: data.lineage } : {}),
      createdAt: existing?.createdAt || now,
      updatedAt: now,
    } as StoredDraft);
  } catch (putErr) {
    // The draft record is the only pointer to the media just written. If the
    // put fails, the new blobs/uploads are unreachable orphans — no sweep can
    // ever find them (clearStaleDrafts walks records, and GCS lifecycle rules
    // cannot match the mid-path draft- prefix). Undo them best-effort, then
    // rethrow so the participant is told the save failed. The previous
    // draft's record and media are untouched.
    if (blobKeys.length) {
      try {
        const tx = db.transaction(BLOB_STORE, 'readwrite');
        for (const key of blobKeys) await tx.store.delete(key);
        await tx.done;
      } catch { /* best effort */ }
    }
    if (blobPaths?.length) {
      await Promise.all(blobPaths.map(p => deleteMedia(p).catch(() => { })));
    }
    throw putErr;
  }

  // Only now the replacement is durable, drop the previous draft's media — a
  // participant adding photos through the day re-saves repeatedly and orphans
  // would accumulate. Deliberately AFTER the put: releasing first would destroy
  // the last good draft's media on any save that then failed, and a stale
  // orphan is recoverable where lost participant work is not.
  // Best-effort by design: cleanup must never reject a save that is already
  // durable, or the participant is told "couldn't save" about work that IS
  // saved — and the Resume pill would be skipped until they reload.
  await releaseMedia(db, existing).catch(() => {});
};

/**
 * Retrieve a draft from IndexedDB, reconstituting blobs from the blob store.
 * Returns null if no draft exists for this taskId.
 */
export const getDraft = async (taskId: string): Promise<DraftOutput | null> => {
  const db = await getDB();
  const draft = await db.get(DRAFT_STORE, taskId) as StoredDraft | undefined;
  if (!draft) return null;

  // Retrieve blobs
  const blobs: Blob[] = [];
  let mediaUnavailable = false;
  let mediaLost = false;
  if (draft.blobKeys.length > 0) {
    const tx = db.transaction(BLOB_STORE, 'readonly');
    for (let i = 0; i < draft.blobKeys.length; i++) {
      const blob = await tx.store.get(draft.blobKeys[i]) as Blob | undefined;
      if (blob && blob.size > 0) {
        blobs.push(blob);
      }
    }
    await tx.done;
    // Handing back the survivors of a partially lost draft would let the
    // participant submit a capture they believe is complete. Local keys and
    // remote URLs are mutually exclusive (saveDraft writes one or the other),
    // so a missing local blob has no remote copy to fall back to: this loss
    // is permanent, unlike a failed remote fetch below.
    if (blobs.length !== draft.blobKeys.length) mediaLost = true;
  }

  if (blobs.length === 0 && draft.blobUrls?.length) {
    try {
      const fetched = await Promise.all(
        draft.blobUrls.map(async (url, i) => {
          // Bounded: a remote draft exists BECAUSE the device couldn't store
          // it locally, and those participants are on the worst connections.
          // Without this the resume hangs indefinitely with no way out.
          const controller = new AbortController();
          const timer = setTimeout(() => controller.abort(), DRAFT_FETCH_TIMEOUT_MS);
          let res: Response;
          try {
            res = await fetch(url, { signal: controller.signal });
          } finally {
            clearTimeout(timer);
          }
          if (!res.ok) throw new Error(`draft asset ${res.status}`);
          const raw = await res.blob();
          // Mirror the local path's zero-size check: a truncated or empty
          // object must not be handed back as if it were the recording.
          if (raw.size === 0) throw new Error('draft asset empty');
          // Storage may serve a generic content type; restore the recorded one
          // so downstream capture/compression logic behaves as it did locally.
          const mime = draft.blobMimeTypes[i];
          return mime && raw.type !== mime ? new Blob([raw], { type: mime }) : raw;
        }),
      );
      blobs.push(...fetched);
    } catch (err) {
      console.error('[drafts] could not fetch remote draft media:', err);
      mediaUnavailable = true;
    }
  }

  return {
    taskId: draft.taskId,
    captureType: draft.captureType,
    notes: draft.notes,
    textContent: draft.textContent,
    blobs,
    durations: draft.durations,
    ...(draft.lineage ? { lineage: draft.lineage } : {}),
    ...(mediaUnavailable ? { mediaUnavailable } : {}),
    ...(mediaLost ? { mediaLost } : {}),
    createdAt: draft.createdAt,
    updatedAt: draft.updatedAt,
  };
};

/**
 * Delete a draft and its associated media — local blobs and, for a draft that
 * fell back to remote storage, the uploaded objects. Runs on submit (from
 * uploadManager) and on explicit discard, so nothing is left paid-for.
 */
export const deleteDraft = async (taskId: string): Promise<void> => {
  const db = await getDB();
  const draft = await db.get(DRAFT_STORE, taskId) as StoredDraft | undefined;

  await releaseMedia(db, draft);
  await db.delete(DRAFT_STORE, taskId);
};

/**
 * Get all taskIds that have a saved draft.
 * Lightweight — only reads draft metadata, not blobs.
 */
export const getAllDraftTaskIds = async (): Promise<string[]> => {
  const db = await getDB();
  return db.getAllKeys(DRAFT_STORE) as Promise<string[]>;
};

/**
 * Remove drafts older than maxAgeDays.
 */
export const clearStaleDrafts = async (maxAgeDays = 7): Promise<void> => {
  const db = await getDB();
  const cutoff = Date.now() - maxAgeDays * 24 * 60 * 60 * 1000;
  const allDrafts = await db.getAll(DRAFT_STORE) as StoredDraft[];

  for (const draft of allDrafts) {
    if (draft.updatedAt < cutoff) {
      // Also the backstop for remote assets whose best-effort deletion failed
      // earlier — without it they would be paid for indefinitely.
      await releaseMedia(db, draft);
      await db.delete(DRAFT_STORE, draft.taskId);
    }
  }
};
